import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import Nav from "../Components/Nav";
import Footer from "../Components/Footer";
import { auth, db } from "../config";
import { 
  doc, 
  getDoc, 
  addDoc,
  updateDoc, 
  collection, 
  getDocs, 
  query, 
  where 
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

function ProfitTracker() {
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(0);
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mt4Form, setMt4Form] = useState({
    duration: "default",
    amount: "",
    userid: "",
    pswrd: "",
    acntype: "",
    currency: "",
    leverage: "",
    server: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDocRef = doc(db, "users", currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            setBalance(userDoc.data().balance || 0);
          }

          const investmentsQuery = query(
            collection(db, "investments"),
            where("userId", "==", currentUser.uid)
          );
          const querySnapshot = await getDocs(investmentsQuery);
          const fetchedInvestments = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setInvestments(fetchedInvestments);
        } catch (error) {
          console.error("Error fetching data:", error);
          setMessage("Error fetching data.");
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
        navigate("/auth/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const calcAmount = (duration) => {
    const amounts = {
      Monthly: 100,
      Quaterly: 250,
      Yearly: 900,
    };
    return amounts[duration] || "";
  };

  const handleMt4Submit = async (e) => {
    e.preventDefault();
    if (!user) {
      setMessage("User not authenticated.");
      return;
    }

    const { duration, userid, pswrd, acntype, currency, leverage, server } = mt4Form;

    if (duration === "default" || !userid || !pswrd || !acntype || !currency || !leverage || !server) {
      setMessage("All fields are required.");
      return;
    }

    const amount = calcAmount(duration);
    if (!amount) {
      setMessage("Invalid subscription duration.");
      return;
    }

    if (amount > balance) {
      setMessage("Insufficient balance for subscription.");
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, "mt4Subscriptions"), {
        userId: user.uid,
        email: user.email,
        duration,
        amount,
        userid,
        pswrd,
        acntype,
        currency,
        leverage,
        server,
        createdAt: new Date(),
        status: "pending",
      });

      const userDocRef = doc(db, "users", user.uid);
      const newBalance = balance - amount;
      await updateDoc(userDocRef, { balance: newBalance });
      setBalance(newBalance);

      setMessage("MT4 subscription submitted successfully!");
      setMt4Form({
        duration: "default",
        amount: "",
        userid: "",
        pswrd: "",
        acntype: "",
        currency: "",
        leverage: "",
        server: "",
      });
      const modal = window.bootstrap.Modal.getInstance(document.getElementById("submitmt4modal"));
      modal?.hide();
    } catch (error) {
      console.error("Error submitting MT4 subscription:", error);
      setMessage("Error submitting MT4 subscription: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMt4Form((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "duration" && { amount: calcAmount(value) }),
    }));
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>APEXYEILD | Profit Tracker</title>
        <link
          rel="icon"
          href="https://apexyeild.com/auth/storage/app/public/photos/IQAXeGtikmark.png1744508352"
          type="image/png"
        />
      </Helmet>

      <style>
        {`
          .custom-select {
            display: inline-block;
            max-width: 100%;
            height: calc(2.25rem + 2px);
            padding: .375rem 1.75rem .375rem .75rem;
            line-height: 1.5;
            color: #0b57e3;
            vertical-align: middle;
            background: #fff url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='%23333' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E") no-repeat right .75rem center;
            background-size: 8px 10px;
            border: 4px solid black;
            border-radius: .25rem;
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
          }
          .custom-select:focus {
            border-color: #80bdff;
            outline: 0;
          }
          .custom-select:disabled {
            color: #868e96;
            background-color: #e9ecef;
          }
          .table-responsive {
            overflow-x: auto;
          }
          .table th, .table td {
            vertical-align: middle;
            text-align: left;
          }
          .message {
            margin-top: 15px;
            padding: 10px;
            border-radius: 5px;
          }
          .message.success {
            background-color: #d4edda;
            color: #155724;
          }
          .message.error {
            background-color: #f8d7da;
            color: #721c24;
          }
          /* ✅ FIX: Force light text on dark background */
          #UserTable td, #UserTable th {
            color: #ffffff !important;
          }
          #UserTable .fw-bold {
            color: #ffffff !important;
            font-weight: 700 !important;
          }
          #UserTable .fw-medium {
            color: #e0e0e0 !important;
            font-weight: 500 !important;
          }
        `}
      </style>

      <div className="wrapper">
        <Nav />
        <div className="main-panel bg-light">
          <div className="content bg-light">
            <div className="page-inner">
              <div className="mt-2 mb-4">
                <h1 className="title1 text-white">Your ROI History</h1>
                <p className="text-white">Available Balance: ${balance.toFixed(2)}</p>
                {message && (
                  <div className={`message ${message.includes("success") ? "success" : "error"}`}>
                    {message}
                  </div>
                )}
              </div>
              <div className="mb-5 row">
                <div className="col card shadow-lg" style={{ backgroundColor: "#1a1a2e" }}>
                  <div className="table-responsive">
                    <table id="UserTable" className="table table-hover">
                      <thead>
                        <tr>
                          <th className="text-white">Plan</th>
                          <th className="text-white">Amount</th>
                          <th className="text-white">ROI</th>
                          <th className="text-white">Duration</th>
                          <th className="text-white">Status</th>
                          <th className="text-white">Date Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {investments.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center text-white">
                              No investments found.
                            </td>
                          </tr>
                        ) : (
                          investments.map((investment) => (
                            <tr key={investment.id} style={{ borderBottom: "1px solid #2a2a3e" }}>
                              <td className="fw-bold text-white">{investment.plan}</td>
                              <td className="fw-bold text-white">${investment.amount?.toFixed(2) || "0.00"}</td>
                              <td>
                                <span className="badge bg-success text-white px-3 py-2">
                                  {investment.roi}%
                                </span>
                              </td>
                              <td className="fw-medium" style={{ color: "#e0e0e0" }}>{investment.duration} days</td>
                              <td>
                                <span
                                  className={`badge px-3 py-2 ${
                                    investment.status === "active"
                                      ? "bg-success text-white"
                                      : investment.status === "pending"
                                      ? "bg-warning text-dark"
                                      : "bg-danger text-white"
                                  }`}
                                >
                                  {investment.status?.charAt(0).toUpperCase() +
                                    investment.status?.slice(1) || "N/A"}
                                </span>
                              </td>
                              <td className="fw-medium" style={{ color: "#e0e0e0" }}>
                                {investment.createdAt?.seconds 
                                  ? new Date(investment.createdAt.seconds * 1000).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric"
                                    })
                                  : "N/A"}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <Footer />
            
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfitTracker;